import axios from 'axios';
import Service from '@advisor/util/Service';

const {MS_BASE, getParams} = Service;

export default class Transactions {
  /** Linked bank accounts for selected client..
   * @param {Object} payload {clientId (mandatory), accountId (optional)}.
   * @link GET: ms/v1/custodian/linkedBankAccounts?clUserId={userId}&accountId={accountId}
   */

  static getLinkedBankAccounts(payload) {
    let params = `?clUserId=${payload.clientId}`;

    params += typeof payload.accountId === 'undefined' ? `` : `&accountId=${payload.accountId}`;

    return axios.get(`${MS_BASE}/v1/custodian/linkedBankAccounts${params}`);
  }

  /** This will fetch scheduled money transfers for selected client/ clients of advisor..
   * @param {Object} payload {clientId (optional), riaUserId (optional), accountId (optional)}.
   * @link GET: ms/v1/custodian/scheduledMoneyTransfers?clUserId={userId}&accountId={accountId}
   */

  static cancelMoneyTransferRequest(payload) {
    return axios.delete(`${MS_BASE}/v1/custodian/moneyTransferRequest/${payload.transferId}`);
  }

  /** Preview a PDF of a signed document using Docusign (for TD)
   * @param {Object} payload {docusignEnvelopeId}.
   * @link GET: ms/v1/custodian/signedTdDocument?docusignEnvelopeId={docusignEnvelopeId}
   */

  static signedTdDocument(payload) {
    const params = getParams(payload, ['docusignEnvelopeId']);

    return axios.get(`${MS_BASE}/v1/custodian/signedTdDocument`, {params});
  }

  /** Scheduled money transfers for selected client/ clients of advisor..
   * @param {Object} payload {clientId (optional), riaUserId (optional), accountId (optional)}.
   * @link GET: ms/v1/custodian/moneyTransferRequest?clUserId={userId}&transferStatus={transferStatus}
   */

  static getMoneyTransfers(payload) {
    let params = [];

    if (payload.clUserId) {
      params = getParams(payload, [
        'clUserId',
        'startDate',
        'endDate',
        'custodian',
        'transferStatus',
        'transactionType',
        'transactionStatus',
        'fullyPopulated',
        'sortField',
        'sortOrder',
        'searchText',
        'offset',
        'limit',
      ]);
    }

    if (payload.riaUserId) {
      params = getParams(payload, [
        'riaUserId',
        'startDate',
        'endDate',
        'custodian',
        'transferStatus',
        'transactionType',
        'transactionStatus',
        'fullyPopulated',
        'sortField',
        'sortOrder',
        'searchText',
        'offset',
        'limit',
      ]);
    }

    return axios.get(`${MS_BASE}/v1/custodian/moneyTransferRequest`, {
      params,
    });
  }

  /** This will fetch the monetary transactions of a client
   * @param {Object} payload {clUserId, offset, limit, transactionCodes}.
   * @link GET: /v1/client/clUsers/{clUserId}/monetaryTransactions
   */

  static monetaryTransactions(payload) {
    const params = getParams(payload, ['transactionCodes', 'start', 'end', 'offset', 'limit']);

    return axios.get(`${MS_BASE}/v1/client/clUsers/${payload.clUserId}/monetaryTransactions`, {params});
  }

  /** This will fetch the history of transactions of all the users of the advisor currently logged
   * @param {Object} payload {riaUserId, offset, limit, transactionCodes}.
   * @link GET: /v1/client/clUsers/{riaUserId}/historyOfTransactions
   */

  static historyOfTransactions(payload) {
    const params = getParams(payload, ['riaUserId', 'transactionCodes', 'start', 'end', 'offset', 'limit']);

    return axios.get(`${MS_BASE}/v1/client/monetaryTransactions`, {params});
  }

  // GET: /v1/custodian/esignatures?riaUserId={riaUserId}
  static eSignatures(payload) {
    return axios.get(
      `${MS_BASE}/v1/custodian/clients/eSignatureDetails?riaId=${payload.riaId}&riaUserId=${payload.riaUserId}`
    );
  }

  /** Preview a PDF of a e-signed document
   * @param {Object} payload {docusignEnvelopeId}.
   * @link GET: ms/v1/custodian//esignDocument/esignDocument/{payload.docusignEnvId}
   */

  static esignDocument(payload) {
    return axios.get(`${MS_BASE}/v1/custodian/esignDocument/${payload.docusignEnvId}`);
  }

  /** Void request for an e-signed document
   * @param {Object} payload {docusignEnvelopeId}.
   * @link GET: ms/v1/custodian/voidDocumentRequest/{docEnvelopeId}
   */

  static voidDocument(payload) {
    const params = getParams(payload, ['riaId']);

    return axios.get(`${MS_BASE}/v1/custodian/voidDocumentRequest/${payload.docEnvelopeId}/`, {params});
  }

  /** Resend request for an e-signed document
   * @param {Object} payload {docusignEnvelopeId}.
   * @link GET: ms/v1/custodian/docusign/{docEnvelopeId}/resend
   */

  static resendDocument(payload) {
    const params = getParams(payload, ['riaId']);

    return axios.get(`${MS_BASE}/v1/custodian/docuSign/${payload.docEnvelopeId}/resend`, {params});
  }

  /** Delete linked Bank
   * @param {Number} bankAccoutId.
   * @link DELETE: /v1/custodian/bank/bankAccountId?bankAccountId=${bankAccountId}
   */
  static deleteLinkedBank(bankAccountId) {
    return axios.delete(`${MS_BASE}/v1/custodian/bank/bankAccountId?bankAccountId=${bankAccountId}`);
  }

  /** Cancel scheduled transfer with note
   * @param {object} {transferId, transferNote}.
   * transferId - required
   * transferNote - optional
   * @link POST /transfers/{transferId}/cancel
   */
  static cancelScheduledTransferRequest(payload) {
    return axios.post(`${MS_BASE}/v1/custodian/transfers/${payload.transferId}/cancel`, payload);
  }

  /** Set skip transfer
   * @param {Object} Payload:
   * {
      transferId,
      skipStartDate,
      skipUntilDate,
      note,
      initiatedFrom,
    }
   * @link POST /v1/custodian/moneyTransferRequest/{transferId}/skipTransfer?riaComponent=CLIENTS
   */

  static setSkipTransfer(payload) {
    return axios.post(`${MS_BASE}/v1/custodian/moneyTransferRequest/${payload.transferId}/skipTransfer`, payload);
  }

  /** Send email to client
   * @param {object} {riaUserId, clUserId, bankTransferId}.
   * @link POST /v1/orchestrate/emails/adviser/{riaUserId}/client/{clientId}/{templateName}
   */
  static sendEmailToClient(payload) {
    return axios.post(
      `${MS_BASE}/v1/orchestrate/emails/adviser/${payload.riaUserId}/client/${payload.clUserId}/${payload.templateName}`,
      payload
    );
  }
}
